import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import useAuth from '@/hooks/useAuth';
import LocalStorageService from '@/services/LocalStorageService';

import { CURRENCY_SYMBOLS } from './currencyConstants';
import type { CurrencyCode } from './currencyConstants';
import { CurrencyContext } from './useCurrency';

const defaultCurrency: CurrencyCode = 'USD';

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const orgId = user?.organization?.id;
  const [currency, setCurrencyState] = useState<CurrencyCode>(defaultCurrency);

  useEffect(() => {
    if (orgId != null) {
      const stored = LocalStorageService.getOrganizationCurrency(orgId);
      setCurrencyState(stored === 'EUR' ? 'EUR' : defaultCurrency);
    } else {
      setCurrencyState(defaultCurrency);
    }
  }, [orgId]);

  const setCurrency = useCallback(
    (newCurrency: CurrencyCode) => {
      setCurrencyState(newCurrency);
      if (orgId != null) {
        LocalStorageService.setOrganizationCurrency(orgId, newCurrency);
      }
    },
    [orgId]
  );

  const value = useMemo(
    () => ({
      currency,
      currencySymbol: CURRENCY_SYMBOLS[currency],
      setCurrency,
    }),
    [currency, setCurrency]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
